import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { trapCatchKeyForInsect } from 'App/dashboards/_utils/pestValueTypeKeys'
import moment from 'moment-timezone'
import { useEffect, useState } from 'react'
import { apiFetch } from 'utils/apiFetch'
import { getIsoWithoutTheZ } from 'utils/getIsoWithoutTheZ'

type TPointsData = NonNullable<routes.Values.Response['points']>

export type TUseTrapCatchPointsResult = {
  data: TPointsData | null
  loading: boolean
  error: Error | null
}

export const useTrapCatchPoints = ({
  propertyId,
  insectId,
  lngLats,
  refreshToken = 0,
}: {
  propertyId: number | null
  insectId: number
  lngLats: string[]
  refreshToken?: number
}): TUseTrapCatchPointsResult => {
  const [data, setData] = useState<TPointsData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  // Stable dep based on contents, so callers can pass a fresh array each render
  // without retriggering the fetch when contents haven't changed.
  const lngLatsKey = lngLats.join('|')

  useEffect(() => {
    if (propertyId === null || lngLats.length === 0) {
      setData(null)

      setLoading(false)

      setError(null)

      return
    }

    let cancelled = false

    setLoading(true)

    setError(null)

    const timezone = moment.tz.guess()
    const dateFrom = moment.tz(timezone).startOf('year')
    const dateTo = moment.tz(timezone).endOf('day')

    const body = {
      dateFrom: getIsoWithoutTheZ(dateFrom),
      dateTo: getIsoWithoutTheZ(dateTo),
      featureReferer: 'DASHBOARD',
      points: {
        lngLats,
        valuesRequested: {
          [trapCatchKeyForInsect(insectId)]: { preferredAggregationInterval: 'DAILY' },
        },
      },
    } as routes.Values.Request

    apiFetch({ url: '/values', body })
      .then((res) => {
        if (cancelled) return

        setData(res.points ?? null)
      })
      .catch((err) => {
        if (cancelled) return

        setError(err instanceof Error ? err : new Error(String(err)))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [propertyId, insectId, lngLatsKey, refreshToken])

  return { data, loading, error }
}
